import Vue from "vue";
import Vuex from "vuex";
import RSSParser from "rss-parser";
import InternalRSS from "@/script/internalRSS";

Vue.use(Vuex);

function preprocessNewsItem(items) {
  items.forEach(item => item.date = Date.parse(item.isoDate));
}

function getCategories(news) {
  var categories = [], index = {}, result = {};
  news.forEach(item => {
    (item.categories || []).forEach((category) => {
      var stat = categories[index[category]];
      if (!stat) {
        stat = { count: 0, latest: null };
        index[category] = categories.length;
        categories.push(stat);
      }
      stat.count++;
      if (!stat.latest || stat.latest.date < item.date) {
        stat.latest = item;
      }
    });
  });
  Object.keys(index).forEach(key => {
    result[key] = categories[index[key]];
  });
  return result;
}

function filterNews(news, filter) {
  var filterResult = news;
  if (filter.category) {
    filterResult = filterResult.filter(item =>
      item.categories.includes(filter.category)
    );
  }
  if (filter.dateRange) {
    filterResult = filterResult.filter(item =>
      item.date >= filter.dateRange[0] && item.date < filter.dateRange[1]
    );
  }
  if (filter.title) {
    filterResult = filterResult.filter(item =>
      item.title.includes(filter.title)
    );
  } else if (filter.titleOrContent) {
    filterResult = filterResult.filter(item =>
      item.title.includes(filter.titleOrContent) || item.contentSnippet.includes(filter.titleOrContent)
    );
  }
  if (filter.link) {
    filterResult = filterResult.filter(item =>
      item.link.includes(filter.link)
    );
  }
  return filterResult;
}

function requestGet(url, onProgress) {
  return new Promise((resolve, reject) => {
    var request = new XMLHttpRequest();
    request.addEventListener("progress", onProgress);
    request.addEventListener("load", () => {
      if (request.status == 200) {
        resolve(request.responseText);
      } else {
        reject(new Error("Failed to fetch content."));
      }
    });
    request.addEventListener("error", () => {
      reject(new Error("Failed to fetch content."));
    });
    request.addEventListener("abort", () => {
      reject(new Error("User aborted."));
    });
    request.open("GET", url);
    request.send();
  })
}

export default new Vuex.Store({
  state: {
    status: "idle",
    progressLoaded: 0,
    progressTotal: -1,
    progressSpeed: 0,
    progressEstimate: 0,
    loadError: null,
    url: null,
    rss: null,
    filter: null,
    currentNews: null,
    detailVisible: false
  },
  getters: {
    rawRSS(state) { // state?.rss || {}
      if (state.status != "ready") return {};
      return state.rss;
    },
    rawNewList(state) { // state?.rss?.items || []
      if (state.status != "ready") return [];
      return state.rss.items || [];
    },
    categories(state, getters) {
      if (state.status != "ready") return [];
      return getCategories(getters.rawNewList);
    },
    newsList(state, getters) {
      var newsList;
      if (state.status != "ready") return [];
      newsList = getters.rawNewList;
      if (state.filter) {
        newsList = filterNews(newsList, state.filter);
      }
      return newsList;
    },
    newsId(state) {
      if (state.status != "ready" || !state.currentNews) return null;
      return state.currentNews.guid;
    },
    historyState(state) {
      var querystring = new URLSearchParams();
      var filter = state.filter || {};
      if (state.url) querystring.set("src", state.url);
      if (state.newsId) querystring.set("news", state.newsId);
      if (filter.category) querystring.set("category", filter.category);
      if (filter.dateRange) {
        querystring.set("dateStart", filter.dateRange[0].toISOString());
        querystring.set("dateEnd", filter.dateRange[1].toISOString());
      }
      if (filter.title) querystring.set("title", filter.title);
      if (filter.titleOrContent) querystring.set("content", filter.titleOrContent);
      if (filter.link) querystring.set("link", filter.link);
      return {
        querystring: querystring.toString(),
        state: {
          url: state.url,
          newsId: state.newsId,
          category: filter.category,
          dateRange: filter.dateRange,
          title: filter.title,
          titleOrContent: filter.titleOrContent,
          link: filter.link
        }
      };
    }
  },
  mutations: {
    startLoad(state, { url }) {
      state.status = "loading";
      state.url = url;
      state.progressLoaded = 0;
      state.progressTotal = NaN;
    },
    progressChange(state, event) {
      state.progressLoaded = event.loaded;
      state.progressSpeed = event.speed;
      if (event.lengthComputable) {
        state.progressTotal = event.total;
        state.progressEstimate = event.estimate;
      } else {
        state.progressTotal = -1;
      }
    },
    dataReady(state, { rss }) {
      state.status = "ready";
      preprocessNewsItem(rss.items);
      state.rss = rss;
    },
    loadFailed(state, { error }) {
      state.status = "error";
      state.loadError = error;
    },
    openNewsDetail(state, { newsItem }) {
      state.currentNews = newsItem;
      state.detailVisible = true;
    },
    closeNewsDetail(state) {
      state.detailVisible = false;
    },
    clearFilter(state) {
      state.filter = null;
    },
    setFilter(state, filter) {
      if (!filter.category
        && !filter.dateRange
        && !filter.title
        && !filter.titleOrContent
        && !filter.link
      ) {
        state.filter = filter = null;
      } else { // TODO: Add optimized property set action, to reduce the frequency of "newsList" refreshing.
        state.filter = filter;
      }
    }
  },
  actions: {
    async load(context, payload) {
      var xml, rss, startTime, lastTime, lastLoaded;
      if (context.state.status == "loading") return;
      context.commit("startLoad", payload);
      try {
        startTime = lastTime = Date.now();
        lastLoaded = 0;
        xml = await requestGet(payload.normalizedUrl, event => {
          var now = Date.now();
          context.commit("progressChange", {
            total: event.total,
            loaded: event.loaded,
            lengthComputable: event.lengthComputable,
            speed: (event.loaded - lastLoaded) / (now - lastTime) * 1000,
            estimate: (event.total - event.loaded) / (event.loaded / (now - startTime) * 1000)
          });
          lastTime = now;
          lastLoaded = event.loaded;
        });
        rss = await (new RSSParser({
          customFields: {
            feed: [["atom:icon", "icon"]],
            item: ["receivedDate"],
          },
        }).parseString(xml));
        context.commit("dataReady", { rss });
      } catch (error) {
        context.commit("loadFailed", { error });
        console.warn(error);
      }
    },
    openNews(context, { newsItem }) {
      context.commit("openNewsDetail", { newsItem });
    },
    openNewsByGuid(context, { guid }) {
      var newsItem;
      if (context.state.status != "ready") return;
      newsItem = context.getters.rawNewList.filter(item => item.guid == guid)[0];
      if (newsItem) {
        context.commit("openNewsDetail", { newsItem });
      }
    },
    closeNews(context) {
      context.commit("closeNewsDetail");
    },
    setCategory(context, { category }) {
      context.commit("setFilter", {
        ...context.state.filter,
        category
      })
    },
    async restoreFromSearch(context, { querystring }) {
      var params = new URLSearchParams(querystring);
      var dateRange = [
        Date.parse(params.get("dateStart")),
        Date.parse(params.get("dateEnd"))
      ];
      if (isNaN(dateRange[0]) && isNaN(dateRange[1])) {
        dateRange = null;
      }
      return context.dispatch("restoreFromState", {
        url: params.get("src"),
        newsId: params.get("news"),
        category: params.get("category"),
        dateRange: dateRange,
        title: params.get("title"),
        titleOrContent: params.get("content"),
        link: params.get("link")
      });
    },
    async restoreFromState(context, newState) {
      var state = context.state;
      context.commit("setFilter", {
        category: newState.category,
        dateRange: newState.dateRange,
        title: newState.title,
        titleOrContent: newState.titleOrContent,
        link: newState.link
      });
      if (!state.url || newState.url != state.url) {
        await context.dispatch("load", {
          url: newState.url,
          normalizedUrl: new URL(newState.url || InternalRSS.about, location.origin).toString()
        })
      }
      if (newState.newsId != context.getters.newsId) {
        if (newState.newsId) {
          await context.dispatch("openNewsByGuid", { guid: newState.newsId });
        } else {
          await context.dispatch("closeNews");
        }
      }
    }
  }
});
